// @ts-check
/* eslint-env browser */

import { openExtractionExportDialog, setDialogError } from './extraction-export-dialog.js'

/**
 * Updates bulk action button labels and disabled state when the extraction
 * list selection changes.
 *
 * Usage:
 *   <extraction-list-select>
 *     <extraction-list-bulk-actions>
 *       <button class="extraction-list__export-selected-button">...</button>
 *       <button class="extraction-list__delete-selected-button">...</button>
 *     </extraction-list-bulk-actions>
 *   </extraction-list-select>
 */
/**
 * @typedef {HTMLElement & {
 *   removeRowById: (id: string) => void,
 *   focusAfterRowRemoval: () => void
 * }} ExtractionListSelectHost
 */
class ExtractionListBulkActions extends HTMLElement {
  /** @type {ExtractionListSelectHost | null} */
  #host = null

  /** @type {EventListener | null} */
  #onSelectionChange = null

  /** @type {string[]} */
  #selectedIds = []

  /** @type {boolean} */
  #isDeleting = false

  connectedCallback () {
    this.#host = /** @type {ExtractionListSelectHost | null} */ (this.closest('extraction-list-select'))
    if (!this.#host) {
      console.warn('extraction-list-bulk-actions: No extraction-list-select host found')
      return
    }

    this.#onSelectionChange = (event) => {
      const customEvent = /** @type {CustomEvent<{count: number, selectedIds: string[]}>} */ (event)
      this.#setSelection(customEvent.detail.selectedIds)
    }

    this.#host.addEventListener('extraction-selection-change', this.#onSelectionChange)

    this.querySelector('.extraction-list__export-selected-button')?.addEventListener('click', () => {
      this.#openExportDialog()
    })

    this.querySelector('.extraction-list__delete-selected-button')?.addEventListener('click', () => {
      this.#openDeleteDialog()
    })

    const initialSelection = /** @type {HTMLInputElement[]} */(
      [...this.#host.querySelectorAll('.extraction-list__select-input:checked')]
    ).map((input) => input.value)
    this.#setSelection(initialSelection)
  }

  disconnectedCallback () {
    if (this.#host && this.#onSelectionChange) {
      this.#host.removeEventListener('extraction-selection-change', this.#onSelectionChange)
    }
  }

  #openExportDialog () {
    if (this.#selectedIds.length === 0) {
      return
    }

    openExtractionExportDialog({
      jobIds: [...this.#selectedIds],
      host: this.#host ?? undefined
    })
  }

  #openDeleteDialog () {
    if (this.#selectedIds.length === 0 || this.#isDeleting) {
      return
    }

    const itemLabel = this.#selectedIds.length === 1 ? 'item' : 'items'
    const wrapper = document.createElement('div')
    wrapper.innerHTML = `
      <modal-dialog>
        <dialog class="modal-dialog">
          <h2 class="modal-dialog__title govuk-heading-m">Delete selected extractions</h2>
          <div class="extraction-delete-dialog__error" role="alert" aria-live="assertive" hidden></div>
          <p class="modal-dialog__message govuk-body">Are you sure you want to delete ${this.#selectedIds.length} ${itemLabel}?</p>
          <div class="govuk-button-group">
            <button type="button" class="govuk-button govuk-button--warning" data-action="confirm" data-async-confirm>Yes</button>
            <button type="button" class="govuk-button govuk-button--secondary" data-action="cancel">No</button>
          </div>
        </dialog>
      </modal-dialog>
    `

    const modalDialog = /** @type {HTMLElement & { close?: () => void } | null} */ (
      wrapper.firstElementChild
    )
    if (!modalDialog) {
      return
    }

    const dialogRoot = modalDialog.querySelector('dialog')
    const errorContainer = /** @type {HTMLElement | null} */ (
      dialogRoot?.querySelector('.extraction-delete-dialog__error')
    )

    document.body.appendChild(modalDialog)

    let confirmInProgress = false
    const handleConfirm = async () => {
      if (confirmInProgress) {
        return
      }
      confirmInProgress = true

      const confirmButton = /** @type {HTMLButtonElement | null} */ (
        dialogRoot?.querySelector('[data-action="confirm"]')
      )
      const cancelButton = /** @type {HTMLButtonElement | null} */ (
        dialogRoot?.querySelector('[data-action="cancel"]')
      )

      if (errorContainer) {
        setDialogError(errorContainer, '')
      }
      if (confirmButton) {
        confirmButton.disabled = true
      }
      if (cancelButton) {
        cancelButton.disabled = true
      }

      const errorMessage = await this.#deleteSelectedRows()
      if (errorMessage) {
        if (errorContainer) {
          setDialogError(errorContainer, errorMessage)
        }
        if (confirmButton) {
          confirmButton.disabled = false
        }
        if (cancelButton) {
          cancelButton.disabled = false
        }
        confirmInProgress = false
        return
      }

      modalDialog.removeEventListener('confirm', handleConfirm)
      modalDialog.close?.()
    }

    modalDialog.addEventListener('confirm', handleConfirm)
  }

  /**
   * @returns {Promise<string | null>}
   */
  async #deleteSelectedRows () {
    if (!this.#host || this.#isDeleting) {
      return null
    }

    const idsToDelete = [...this.#selectedIds]
    if (idsToDelete.length === 0) {
      return null
    }

    this.#isDeleting = true
    this.#refreshButtons()

    try {
      const response = await fetch('/api/jobs/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_ids: idsToDelete })
      })

      if (!response.ok) {
        return `Could not delete ${idsToDelete.length === 1 ? '1 item' : `${idsToDelete.length} items`}. Please try again.`
      }

      for (const id of idsToDelete) {
        this.#host.removeRowById(id)
      }
    } catch {
      const failureLabel = idsToDelete.length === 1 ? '1 item' : `${idsToDelete.length} items`
      return `Could not delete ${failureLabel}. Please try again.`
    } finally {
      this.#isDeleting = false
      this.#refreshButtons()
      this.#host.focusAfterRowRemoval()
    }

    return null
  }

  /** @param {number} count */
  #updateButtons (count) {
    const exportButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('.extraction-list__export-selected-button'))
    const deleteButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('.extraction-list__delete-selected-button'))

    if (exportButton) {
      exportButton.textContent = `Export selected (${count})`
      exportButton.disabled = this.#isDeleting || count === 0
    }

    if (deleteButton) {
      deleteButton.textContent = `Delete selected (${count})`
      deleteButton.disabled = this.#isDeleting || count === 0
    }
  }

  /**
   * @param {string[]} selectedIds
   */
  #setSelection (selectedIds) {
    this.#selectedIds = selectedIds
    this.#refreshButtons()
  }

  #refreshButtons () {
    this.#updateButtons(this.#selectedIds.length)
  }
}

customElements.define('extraction-list-bulk-actions', ExtractionListBulkActions)
